import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QuillEditor } from '@/components/QuillEditor';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye } from 'lucide-react';

export default function AdminArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [article, setArticle] = useState({
    title: '',
    slug: '',
    category_id: '',
    author: 'Paco Herrera',
    excerpt: '',
    content: '',
    hero_image: '',
    status: 'draft',
    featured: false,
    tags: [],
    faq_items: [],
    seo_title: '',
    seo_description: '',
    og_image: '',
    related_article_ids: []
  });
  
  // Debug logging
  useEffect(() => {
    console.log('Article state changed:', {
      id: article.title,
      contentLength: article.content?.length || 0
    });
  }, [article]);

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [id, navigate]);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const [categoriesRes, articlesRes] = await Promise.all([
        fetch(`${backendUrl}/api/journal/categories`),
        fetch(`${backendUrl}/api/admin/journal/articles`)
      ]);
      setCategories(await categoriesRes.json());
      const articles = await articlesRes.json();
      setAllArticles(articles);

      if (id) {
        const articleData = articles.find(a => a.id === id);
        if (articleData) {
          console.log('Loading article for edit:', { 
            id: articleData.id, 
            title: articleData.title,
            contentLength: articleData.content?.length || 0,
            contentPreview: articleData.content?.substring(0, 100),
            allKeys: Object.keys(articleData)
          });
          setArticle(articleData);
        } else {
          console.error('Article not found with id:', id);
        }
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const calculateReadingTime = (content) => {
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.ceil(words / 200); // Average reading speed
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/journal/upload-image`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      return data.url;
    } catch (err) {
      toast.error('Image upload failed');
      throw err;
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await handleImageUpload(file);
      setArticle({ ...article, hero_image: url });
      toast.success('Image uploaded');
    } catch (err) {
      // Error already toasted
    }
  };

  const handleSave = async (publish = false) => {
    if (!article.title || !article.slug || !article.category_id) {
      toast.error('Please fill in title, slug, and category');
      return;
    }

    setLoading(true);
    try {
      const articleData = {
        ...article,
        reading_time: calculateReadingTime(article.content),
        status: publish ? 'published' : 'draft',
        published_at: publish ? new Date().toISOString() : article.published_at
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = id 
        ? `${backendUrl}/api/admin/journal/articles/${id}`
        : `${backendUrl}/api/admin/journal/articles`;
      
      const method = id ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(articleData)
      });

      toast.success(publish ? 'Article published!' : 'Article saved as draft');
      navigate('/admin/journal');
    } catch (err) {
      toast.error('Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/journal')}>
              <ArrowLeft size={16} />
            </Button>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))]">
              {id ? 'Edit Article' : 'New Article'}
            </h1>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => handleSave(false)} disabled={loading}>
              <Save size={16} className="mr-2" /> Save Draft
            </Button>
            <Button onClick={() => handleSave(true)} disabled={loading} className="bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
              <Eye size={16} className="mr-2" /> Publish
            </Button>
          </div>
        </div>

        <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-8 space-y-6">
          {/* Basic Fields */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Title *</Label>
              <Input
                value={article.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setArticle({ 
                    ...article, 
                    title,
                    slug: generateSlug(title),
                    seo_title: title
                  });
                }}
                className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
            </div>

            <div>
              <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Slug *</Label>
              <Input
                value={article.slug}
                onChange={(e) => setArticle({ ...article, slug: e.target.value })}
                className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Category *</Label>
              <Select value={article.category_id} onValueChange={(v) => setArticle({ ...article, category_id: v })}>
                <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Author</Label>
              <Select value={article.author} onValueChange={(v) => setArticle({ ...article, author: v })}>
                <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                  <SelectItem value="Paco Herrera">Paco Herrera</SelectItem>
                  <SelectItem value="Baklight">Baklight</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Excerpt</Label>
            <Textarea
              value={article.excerpt}
              onChange={(e) => setArticle({ ...article, excerpt: e.target.value })}
              rows={3}
              className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
            />
          </div>

          <div>
            <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Hero Image</Label>
            <input type="file" accept="image/*" onChange={handleHeroImageUpload} className="text-[hsl(var(--bk-fg))] mb-2" />
            {article.hero_image && (
              <img src={article.hero_image} alt="Preview" className="w-32 h-32 object-cover rounded" />
            )}
          </div>

          <div className="flex items-center gap-4">
            <Switch checked={article.featured} onCheckedChange={(v) => setArticle({ ...article, featured: v })} />
            <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Featured Article</Label>
          </div>

          {/* Quill Editor */}
          <div>
            <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Content</Label>
            {/* Only render QuillEditor after article data is loaded (for edit mode) or immediately (for new mode) */}
            {(!id || article.id) && (
              <QuillEditor
                key={article.id || 'new'}
                value={article.content}
                onChange={(content) => setArticle({ ...article, content })}
                onImageUpload={handleImageUpload}
              />
            )}
            {id && !article.id && (
              <div className="bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded p-8 text-center text-[hsl(var(--bk-fg-muted))]">
                Loading editor...
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="border-t border-[hsl(var(--bk-line))] pt-6 mt-6">
            <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-4">Topics & Discovery</h3>
            <div className="space-y-4">
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Tags / Keywords</Label>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-2">
                  Enter comma-separated tags (e.g., "cinematography, mexico, documentary"). Used for SEO and displayed on article page.
                </p>
                <Input
                  value={article.tags ? article.tags.join(', ') : ''}
                  onChange={(e) => {
                    const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                    setArticle({ ...article, tags });
                  }}
                  placeholder="cinematography, mexico, documentary, color grading"
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                />
              </div>
              
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">FAQ Items (Optional)</Label>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-3">
                  Add question/answer pairs for Google FAQ rich snippets
                </p>
                
                {/* FAQ Items List */}
                <div className="space-y-3">
                  {(article.faq_items || []).map((faq, index) => (
                    <div key={index} className="p-4 bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <span className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">FAQ #{index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newFaqs = article.faq_items.filter((_, i) => i !== index);
                            setArticle({ ...article, faq_items: newFaqs });
                          }}
                          className="h-6 px-2 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-[hsl(var(--bk-fg-muted))] mb-1">Question</Label>
                          <Input
                            value={faq.question || ''}
                            onChange={(e) => {
                              const newFaqs = [...article.faq_items];
                              newFaqs[index] = { ...newFaqs[index], question: e.target.value };
                              setArticle({ ...article, faq_items: newFaqs });
                            }}
                            placeholder="What camera was used for this project?"
                            className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-[hsl(var(--bk-fg-muted))] mb-1">Answer</Label>
                          <Textarea
                            value={faq.answer || ''}
                            onChange={(e) => {
                              const newFaqs = [...article.faq_items];
                              newFaqs[index] = { ...newFaqs[index], answer: e.target.value };
                              setArticle({ ...article, faq_items: newFaqs });
                            }}
                            placeholder="We used the ARRI Alexa Mini with Zeiss Supreme lenses..."
                            rows={2}
                            className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add FAQ Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newFaqs = [...(article.faq_items || []), { question: '', answer: '' }];
                      setArticle({ ...article, faq_items: newFaqs });
                    }}
                    className="w-full border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] hover:bg-[hsl(var(--bk-bg-elev))]"
                  >
                    + Add FAQ Item
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* SEO Fields */}
          <div className="border-t border-[hsl(var(--bk-line))] pt-6 mt-6">
            <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-4">SEO & Metadata</h3>
            <div className="space-y-4">
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">SEO Title</Label>
                <Input
                  value={article.seo_title}
                  onChange={(e) => setArticle({ ...article, seo_title: e.target.value })}
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                />
              </div>
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">SEO Description</Label>
                <Textarea
                  value={article.seo_description}
                  onChange={(e) => setArticle({ ...article, seo_description: e.target.value })}
                  rows={2}
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
